import type {
  AdAttributeMappingDto,
  AdSettingsDto,
  AdStatusDto,
  AuthConfigDto,
  AuthenticatedUser,
  CreateAdAttributeMappingInput,
  CreateFieldDefinitionInput,
  CreateFieldSectionInput,
  CreateInviteInput,
  CreateModuleGrantInput,
  CreatePermissionGrantInput,
  BrandAssetKind,
  CreateInstitutionLocationInput,
  CreatePersonInput,
  DashboardCardDto,
  DashboardFilterFieldDto,
  DeletedPersonDto,
  EffectivePermissionsDto,
  InstitutionBrandingDto,
  InstitutionLocationDto,
  InstitutionSettingsDto,
  UpdateInstitutionLocationInput,
  UpdateInstitutionSettingsInput,
  ModuleGrantDto,
  MyAccessDto,
  FieldDefinitionDto,
  FieldSectionDto,
  FieldsSchemaDto,
  InvitePrefill,
  PermissionGrantDto,
  OnboardingFieldDto,
  CreateParkingAssignmentInput,
  OnboardingStatsDto,
  OnboardingSubmissionDto,
  ParkingAssignmentDto,
  PeopleAnalyticsDto,
  PersonDetailDto,
  UpdateParkingAssignmentInput,
  PersonFieldHistoryDto,
  PersonSummaryDto,
  RegistrationInviteDto,
  ResolvedDashboardCardDto,
  SaveDashboardCardsInput,
  DashboardLayoutDto,
  SaveDashboardLayoutInput,
  DashboardSubjectsDto,
  DashboardSubjectType,
  SimilarPersonDto,
  SelfRegistrationInput,
  SetFieldValueInput,
  TeamsOverviewDto,
  TeamsSyncConfigDto,
  TeamsSyncResultDto,
  UpdateTeamsSyncConfigInput,
  ArticlesOverviewDto,
  ArticlesSyncConfigDto,
  ArticlesSyncStatusDto,
  UpdateArticlesSyncConfigInput,
  SistradeStatusDto,
  SistradeSyncStatusDto,
  UpdateAdSettingsInput,
  UpdateFieldDefinitionInput,
  UpdateFieldSectionInput,
  UpdatePersonInput,
  UsernameSuggestionDto,
  TranslateTextInput,
  TranslateTextResult,
  TranslateMissingResult,
  NotificationsSummaryDto,
  PortalModule,
  AdminItMemberDto,
  AddAdminItMemberInput,
  ImpersonationTargetDto,
  StartImpersonationInput,
  AttendanceStatusDto,
  AttendanceOverviewDto,
} from '@ciimar/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  // Auth is a same-origin httpOnly session cookie; include it explicitly.
  const response = await fetch(`/api${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    // Not JSON — typically an HTML error page from the reverse proxy when the
    // request timed out (e.g. a long SISTRADE sync). Surface a clear message
    // instead of a raw "Unrecognized token '<'".
    const timeout = response.status === 502 || response.status === 504;
    throw new ApiError(
      response.status,
      timeout || !response.ok
        ? `O pedido não foi concluído (${response.status}). Pode ter demorado demasiado — tenta novamente.`
        : 'Resposta inválida do servidor.',
    );
  }
  if (!response.ok) {
    const msg = (data as { message?: string | string[] } | undefined)?.message;
    const message = msg
      ? Array.isArray(msg)
        ? msg.join(', ')
        : msg
      : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return data as T;
}

/** Full path to the backend login endpoint (a top-level browser navigation). */
export function loginUrl(returnTo?: string): string {
  const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
  return `/api/auth/login${qs}`;
}

/** Fields a staff member fills when entering a submission directly. */
export interface DirectSubmissionInput {
  fullName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  researchGroup?: string;
  startDate?: string;
  notes?: string;
}

export const api = {
  // Auth
  authConfig() {
    return request<AuthConfigDto>('/auth/config');
  },
  me() {
    return request<AuthenticatedUser>('/auth/me');
  },
  passwordLogin(username: string, password: string) {
    return request<AuthenticatedUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  logout() {
    return request<{ logoutUrl?: string }>('/auth/logout', { method: 'POST' });
  },

  // Notifications (in-app bell)
  notificationsSummary() {
    return request<NotificationsSummaryDto>('/notifications/summary');
  },
  markNotificationsSeen(module: PortalModule) {
    return request<void>('/notifications/seen', {
      method: 'POST',
      body: JSON.stringify({ module }),
    });
  },

  // IT administration: Admin-IT list + impersonation
  adminItStatus() {
    return request<{ isAdminIt: boolean }>('/admin-it/status');
  },
  adminItMembers() {
    return request<AdminItMemberDto[]>('/admin-it/members');
  },
  addAdminItMember(input: AddAdminItMemberInput) {
    return request<AdminItMemberDto>('/admin-it/members', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  removeAdminItMember(username: string) {
    return request<void>(`/admin-it/members/${encodeURIComponent(username)}`, { method: 'DELETE' });
  },
  impersonationTargets(q?: string) {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<ImpersonationTargetDto[]>(`/admin-it/impersonation/targets${qs}`);
  },
  startImpersonation(input: StartImpersonationInput) {
    return request<AuthenticatedUser>('/admin-it/impersonation/start', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  stopImpersonation() {
    return request<AuthenticatedUser>('/admin-it/impersonation/stop', { method: 'POST' });
  },

  // Attendance (Registo Presenças)
  attendanceStatus() {
    return request<AttendanceStatusDto>('/attendance/status');
  },
  attendanceOverview(personId?: string) {
    const qs = personId ? `?personId=${encodeURIComponent(personId)}` : '';
    return request<AttendanceOverviewDto>(`/attendance${qs}`);
  },

  // Invites
  validateInvite(params: { token?: string; code?: string }) {
    const qs = new URLSearchParams();
    if (params.token) qs.set('token', params.token);
    if (params.code) qs.set('code', params.code);
    return request<InvitePrefill>(`/invites/validate?${qs.toString()}`);
  },
  createInvite(input: CreateInviteInput) {
    return request<RegistrationInviteDto>('/invites', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listMyInvites() {
    return request<RegistrationInviteDto[]>('/invites/mine');
  },
  listInvites() {
    return request<RegistrationInviteDto[]>('/invites');
  },
  cancelInvite(id: string) {
    return request<RegistrationInviteDto>(`/invites/${id}/cancel`, { method: 'POST' });
  },

  // Public registration (invite-only, by token or code)
  onboardingFields() {
    return request<OnboardingFieldDto[]>('/onboarding/fields');
  },
  selfRegister(input: SelfRegistrationInput & { formStartedAt?: number }) {
    return request<{ id: string; status: string }>('/onboarding/self-register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Submissions
  createSubmission(input: DirectSubmissionInput) {
    return request<OnboardingSubmissionDto>('/onboarding', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listSubmissions(params: { search?: string; status?: string } = {}) {
    return request<OnboardingSubmissionDto[]>(`/onboarding${queryString(params)}`);
  },
  listMyTeam(params: { search?: string; status?: string } = {}) {
    return request<OnboardingSubmissionDto[]>(`/onboarding/mine${queryString(params)}`);
  },
  stats() {
    return request<OnboardingStatsDto>('/onboarding/stats');
  },
  getSubmission(id: string) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}`);
  },
  updateSubmission(id: string, patch: Partial<OnboardingSubmissionDto>) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  approve(id: string, note?: string) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  reject(id: string, note?: string) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  requestChanges(id: string, note: string) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}/request-changes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  markLaunched(id: string, payload: { sistradeReference?: string; note?: string } = {}) {
    return request<OnboardingSubmissionDto>(`/onboarding/${id}/mark-launched`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Collaborator record: the configurable field engine
  fieldsSchema() {
    return request<FieldsSchemaDto>('/fields/schema');
  },
  /** Fill every missing English label of the ficha (sections/fields/options). */
  translateMissingFieldLabels() {
    return request<TranslateMissingResult>('/fields/translate-missing', { method: 'POST' });
  },
  createFieldDefinition(input: CreateFieldDefinitionInput) {
    return request<FieldDefinitionDto>('/fields/definitions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateFieldDefinition(id: string, patch: UpdateFieldDefinitionInput) {
    return request<FieldDefinitionDto>(`/fields/definitions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteFieldDefinition(id: string) {
    return request<FieldDefinitionDto>(`/fields/definitions/${id}`, { method: 'DELETE' });
  },
  reorderFieldDefinition(id: string, direction: 'up' | 'down') {
    return request<FieldDefinitionDto>(`/fields/definitions/${id}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    });
  },
  createFieldSection(input: CreateFieldSectionInput) {
    return request<FieldSectionDto>('/fields/sections', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  reorderFieldSection(key: string, direction: 'up' | 'down') {
    return request<FieldSectionDto>(`/fields/sections/${key}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    });
  },

  // Collaborator record: people
  listPeople(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<PersonSummaryDto[]>(`/people${qs}`);
  },
  getPerson(id: string) {
    return request<PersonDetailDto>(`/people/${id}`);
  },
  personHistory(id: string) {
    return request<PersonFieldHistoryDto[]>(`/people/${id}/history`);
  },
  /** Same-origin URL for a collaborator's photo (auth cookie sent by the browser). */
  personPhotoUrl(id: string) {
    return `/api/people/${id}/photo`;
  },
  /** Dropdown options for the reference (coded) fields, by definition id. */
  personFieldOptions() {
    return request<Record<string, string[]>>(`/people/field-options`);
  },
  /** Collaborator-focused analytics for the Painel. */
  peopleAnalytics() {
    return request<PeopleAnalyticsDto>(`/people/analytics`);
  },
  /** Collaborators whose name is identical/similar to `name` (duplicate guard). */
  similarPeople(name: string) {
    return request<SimilarPersonDto[]>(`/people/similar?name=${encodeURIComponent(name)}`);
  },
  /** Soft-delete a collaborator (only within 72h of creation). */
  deletePerson(id: string) {
    return request<void>(`/people/${id}`, { method: 'DELETE' });
  },
  /** Admin-only deleted bin. */
  listDeletedPeople() {
    return request<DeletedPersonDto[]>(`/people/deleted`);
  },
  /** Admin-only restore of a soft-deleted collaborator. */
  restorePerson(id: string) {
    return request<void>(`/people/${id}/restore`, { method: 'POST' });
  },

  // Viaturas / estacionamento
  listParking(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<ParkingAssignmentDto[]>(`/parking${qs}`);
  },
  parkingLocations() {
    return request<string[]>(`/parking/locations`);
  },
  createParking(input: CreateParkingAssignmentInput) {
    return request<ParkingAssignmentDto>(`/parking`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateParking(id: string, patch: UpdateParkingAssignmentInput) {
    return request<ParkingAssignmentDto>(`/parking/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteParking(id: string) {
    return request<void>(`/parking/${id}`, { method: 'DELETE' });
  },

  // Configuração Global (instituição + locais + branding)
  institutionBranding() {
    return request<InstitutionBrandingDto>(`/institution/branding`);
  },
  institutionLocations() {
    return request<InstitutionLocationDto[]>(`/institution/locations`);
  },
  institutionSettings() {
    return request<InstitutionSettingsDto>(`/institution`);
  },
  updateInstitution(input: UpdateInstitutionSettingsInput) {
    return request<InstitutionSettingsDto>(`/institution`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /** Machine-translate admin-defined labels (PT<->EN) via the server. */
  translate(input: TranslateTextInput) {
    return request<TranslateTextResult>(`/i18n/translate`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  setInstitutionAsset(kind: BrandAssetKind, dataUrl: string | null) {
    return request<InstitutionSettingsDto>(`/institution/asset/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ dataUrl }),
    });
  },
  createInstitutionLocation(input: CreateInstitutionLocationInput) {
    return request<InstitutionLocationDto>(`/institution/locations`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateInstitutionLocation(id: string, patch: UpdateInstitutionLocationInput) {
    return request<InstitutionLocationDto>(`/institution/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteInstitutionLocation(id: string) {
    return request<void>(`/institution/locations/${id}`, { method: 'DELETE' });
  },

  // Equipa (equipas + linhas de investigação, espelho do SISTRADE)
  teamsOverview() {
    return request<TeamsOverviewDto>(`/teams`);
  },
  syncTeams() {
    return request<TeamsSyncResultDto>(`/teams/sync`, { method: 'POST' });
  },
  teamsSyncConfig() {
    return request<TeamsSyncConfigDto>(`/teams/sync/config`);
  },
  updateTeamsSyncConfig(input: UpdateTeamsSyncConfigInput) {
    return request<TeamsSyncConfigDto>(`/teams/sync/config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /** Produção Científica: a filtered, paginated page of the article mirror. */
  articlesOverview(
    params: {
      q?: string;
      year?: number;
      month?: number;
      type?: string;
      openAccess?: boolean;
      cited?: boolean;
      person?: string;
      team?: string;
      quartile?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.year) qs.set('year', String(params.year));
    if (params.month) qs.set('month', String(params.month));
    if (params.type) qs.set('type', params.type);
    if (params.openAccess) qs.set('openAccess', 'true');
    if (params.cited) qs.set('cited', 'true');
    if (params.person) qs.set('person', params.person);
    if (params.team) qs.set('team', params.team);
    if (params.quartile) qs.set('quartile', params.quartile);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return request<ArticlesOverviewDto>(`/articles${q ? `?${q}` : ''}`);
  },
  /** Start a background articles sync; returns the initial progress. */
  startArticlesSync() {
    return request<ArticlesSyncStatusDto>(`/articles/sync`, { method: 'POST' });
  },
  /** Poll the running sync's progress. */
  articlesSyncStatus() {
    return request<ArticlesSyncStatusDto>(`/articles/sync/status`);
  },
  articlesSyncConfig() {
    return request<ArticlesSyncConfigDto>(`/articles/sync/config`);
  },
  updateArticlesSyncConfig(input: UpdateArticlesSyncConfigInput) {
    return request<ArticlesSyncConfigDto>(`/articles/sync/config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /** Painel builder: the resolved cards for the signed-in user. */
  dashboardCards() {
    return request<ResolvedDashboardCardDto[]>(`/dashboard/cards`);
  },
  /** The signed-in user's own Painel arrangement (tile order + size). */
  dashboardLayout() {
    return request<DashboardLayoutDto>(`/dashboard/layout`);
  },
  saveDashboardLayout(input: SaveDashboardLayoutInput) {
    return request<DashboardLayoutDto>(`/dashboard/layout`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /** Painel builder (admin): the dimensions + values a count can filter on. */
  dashboardFilterFields() {
    return request<DashboardFilterFieldDto[]>(`/dashboard/filter-fields`);
  },
  /** Painel builder (admin): the targets (roles/groups/people) cards can go to. */
  dashboardSubjects() {
    return request<DashboardSubjectsDto>(`/dashboard/subjects`);
  },
  /** Painel builder (admin): the raw configured cards for a subject. */
  dashboardConfig(subjectType: DashboardSubjectType, subjectId: string) {
    const qs = new URLSearchParams({ subjectType, subjectId });
    return request<DashboardCardDto[]>(`/dashboard/config?${qs.toString()}`);
  },
  /** Painel builder (admin): replace the whole card set for a subject. */
  saveDashboardConfig(input: SaveDashboardCardsInput) {
    return request<DashboardCardDto[]>(`/dashboard/config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  createPerson(input: CreatePersonInput) {
    return request<PersonDetailDto>('/people', { method: 'POST', body: JSON.stringify(input) });
  },
  updatePerson(id: string, patch: UpdatePersonInput) {
    return request<PersonDetailDto>(`/people/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  setPersonValues(id: string, values: SetFieldValueInput[]) {
    return request<PersonDetailDto>(`/people/${id}/values`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    });
  },

  // SISTRADE integration
  sistradeStatus() {
    return request<SistradeStatusDto>('/sistrade/status');
  },
  /** Start the sync in the background; returns the initial job state. */
  startSistradeSync() {
    return request<SistradeSyncStatusDto>('/sistrade/sync/employees', { method: 'POST' });
  },
  /** Poll the background sync job state. */
  sistradeSyncStatus() {
    return request<SistradeSyncStatusDto>('/sistrade/sync/status');
  },

  // Fields: section reserved classification
  updateFieldSection(key: string, patch: UpdateFieldSectionInput) {
    return request<FieldSectionDto>(`/fields/sections/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  // Permissions
  myPermissions() {
    return request<EffectivePermissionsDto>('/permissions/me');
  },
  listGrants() {
    return request<PermissionGrantDto[]>('/permissions/grants');
  },
  createGrant(input: CreatePermissionGrantInput) {
    return request<PermissionGrantDto>('/permissions/grants', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteGrant(id: string) {
    return request<void>(`/permissions/grants/${id}`, { method: 'DELETE' });
  },

  // Permissions: module access (permission-driven navigation)
  myModuleAccess() {
    return request<MyAccessDto>('/permissions/modules/me');
  },
  listModuleGrants() {
    return request<ModuleGrantDto[]>('/permissions/modules/grants');
  },
  moduleGrantsForPerson(personId: string) {
    return request<ModuleGrantDto[]>(`/permissions/modules/grants/person/${personId}`);
  },
  createModuleGrant(input: CreateModuleGrantInput) {
    return request<ModuleGrantDto>('/permissions/modules/grants', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateModuleGrant(id: string, canEdit: boolean) {
    return request<ModuleGrantDto>(`/permissions/modules/grants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ canEdit }),
    });
  },
  deleteModuleGrant(id: string) {
    return request<void>(`/permissions/modules/grants/${id}`, { method: 'DELETE' });
  },

  // Active Directory provisioning (config)
  adStatus() {
    return request<AdStatusDto>('/ad/status');
  },
  adSettings() {
    return request<AdSettingsDto>('/ad/settings');
  },
  updateAdSettings(patch: UpdateAdSettingsInput) {
    return request<AdSettingsDto>('/ad/settings', { method: 'PUT', body: JSON.stringify(patch) });
  },
  adMappings() {
    return request<AdAttributeMappingDto[]>('/ad/mappings');
  },
  createAdMapping(input: CreateAdAttributeMappingInput) {
    return request<AdAttributeMappingDto>('/ad/mappings', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteAdMapping(id: string) {
    return request<void>(`/ad/mappings/${id}`, { method: 'DELETE' });
  },
  suggestUsername(firstName: string, lastName: string) {
    return request<UsernameSuggestionDto>('/ad/suggest-username', {
      method: 'POST',
      body: JSON.stringify({ firstName, lastName }),
    });
  },
};

function queryString(params: { search?: string; status?: string }): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  return qs.toString() ? `?${qs.toString()}` : '';
}
